import React, { useEffect, useState, useMemo } from "react";
import {
    collection,
    query,
    onSnapshot,
    doc,
    updateDoc,
    serverTimestamp,
    orderBy,
    Timestamp
} from "firebase/firestore";
import { db } from "../../firebase";

export default function AdminMatchRequests({
    clubId,
    sessionId,
    players,
    setManualSelection,
    playerList
}) {
    const [requests, setRequests] = useState([]);
    const [isOpen, setIsOpen] = useState(true);

    const playerMap = useMemo(() => {
        const map = {};
        players?.forEach(p => { map[p.id] = p; });
        return map;
    }, [players]);

    const playerListMap = useMemo(() => {
        const map = {};
        playerList?.forEach(p => { map[p.id] = p; });
        return map;
    }, [playerList]);

    const getName = (id) => playerListMap[id]?.name || "Unknown";

    useEffect(() => {
        if (!clubId || !sessionId) return;
        const q = query(
            collection(db, "clubs", clubId, "sessions", sessionId, "matchRequests"),
            orderBy("createdAt", "desc")
        );

        return onSnapshot(q, (snap) => {
            setRequests(snap.docs.map(d => ({ id: d.id, ...d.data() })));
        });
    }, [clubId, sessionId]);

    const activeRequests = requests.filter(r => r.status === 'pending' || r.status === 'ready');
    const readyCount = requests.filter(r => r.status === 'ready').length;

    const cancelRequest = async (req) => {
        const expireTime = Timestamp.fromDate(new Date(Date.now() + 5 * 60 * 1000));
        const ref = doc(db, "clubs", clubId, "sessions", sessionId, "matchRequests", req.id);
        await updateDoc(ref, { status: "cancelled", cancelledAt: serverTimestamp(), expiresAt: expireTime });
    };

    const createMatch = async (req) => {
        const playersList = req.players.map(id => playerMap[id]).filter(Boolean);
        const ref = doc(db, "clubs", clubId, "sessions", sessionId, "matchRequests", req.id);
        await updateDoc(ref, { status: "completed", completedAt: serverTimestamp() });
        setManualSelection(playersList);
    };

    return (
        <div className="mb-4">
            <details open={isOpen} onToggle={(e) => setIsOpen(e.target.open)} className="admin-requests-details">
                <summary className="d-flex align-items-center justify-content-between p-3 rounded-4 bg-body-tertiary border shadow-sm list-unstyled cursor-pointer">
                    <div className="d-flex align-items-center gap-3">
                        <div className="position-relative">
                            <div className={`rounded-circle bg-primary bg-opacity-10 d-flex align-items-center justify-content-center ${readyCount > 0 ? 'pulse-icon' : ''}`} style={{ width: '42px', height: '42px' }}>
                                <i className={`bi ${readyCount > 0 ? 'bi-lightning-charge-fill' : 'bi-person-lines-fill'} text-primary fs-5`}></i>
                            </div>
                            {activeRequests.length > 0 && (
                                <span className="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger border border-2 border-body" style={{ fontSize: '0.65rem' }}>
                                    {activeRequests.length}
                                </span>
                            )}
                        </div>
                        <div>
                            <h6 className="mb-0 fw-bold text-body">Match Request Queue</h6>
                            <small className="text-body-secondary">
                                {readyCount > 0 ? `${readyCount} match ready to start` : 'Monitor challenge requests'}
                            </small>
                        </div>
                    </div>
                    <i className={`bi bi-chevron-down transition-all ${isOpen ? 'rotate-180' : ''} text-body-secondary`}></i>
                </summary>

                <div className="pt-3 mt-1">
                    {requests.length === 0 ? (
                        <div className="p-4 text-center rounded-4 border border-dashed text-body-secondary small">
                            No recent match requests
                        </div>
                    ) : (
                        <div className="row g-3">
                            {requests.map(req => {
                                const isReady = req.status === "ready";
                                const isDeclined = req.status === "declined";
                                const isCancelled = req.status === "cancelled";
                                const isCompleted = req.status === "completed";

                                const playersInQueuePositions = req.players.map(id =>
                                    players.findIndex(p => p.id === id)
                                );

                                const canStart = isReady && playersInQueuePositions.every(idx => idx !== -1 && idx < 8);
                                const playersAtTopCount = playersInQueuePositions.filter(idx => idx !== -1 && idx < 8).length;

                                return (
                                    <div key={req.id} className="col-12 col-md-4 col-xl-3">
                                        <div className={`card h-100 border-0 shadow-sm rounded-4 overflow-hidden bg-body-tertiary transition-all ${canStart ? 'ready-card-glow' : ''}`}>

                                            {/* Header Strip */}
                                            <div className={`px-3 py-2 small fw-bold text-uppercase d-flex justify-content-between align-items-center ${canStart ? 'bg-success text-white' :
                                                    isReady ? 'bg-warning text-dark' :
                                                        isDeclined ? 'bg-danger text-white' :
                                                            isCompleted ? 'bg-success-subtle text-success' : // New Completed Style
                                                                isCancelled ? 'bg-secondary text-white' : 'bg-primary text-white'
                                                }`}>
                                                <span>{isReady && !canStart ? 'Wait for Slots' : req.status}</span>
                                                <span className="opacity-75">{req.accepted?.length || 0}/4 Ready</span>
                                            </div>

                                            <div className="card-body p-3 d-flex flex-column">
                                                {/* VS Box */}
                                                <div className="bg-body rounded-4 p-3 mb-2 border border-secondary-subtle border-opacity-10">
                                                    <div className="row align-items-center g-0">
                                                        <div className="col text-center">
                                                            {req.teamA?.map(p => <div key={p} className="small fw-bold text-body text-truncate">{getName(p)}</div>)}
                                                        </div>
                                                        <div className="col-auto px-2 fw-black opacity-25 italic text-body">VS</div>
                                                        <div className="col text-center">
                                                            {req.teamB?.map(p => <div key={p} className="small fw-bold text-body text-truncate">{getName(p)}</div>)}
                                                        </div>
                                                    </div>
                                                </div>

                                                {/* Status/Progress Area (Fixed height to prevent button jumping) */}
                                                <div style={{ minHeight: '45px' }} className="mb-2 d-flex flex-column justify-content-center">
                                                    {isReady && !canStart && (
                                                        <div className="px-1">
                                                            <div className="d-flex justify-content-between mb-1" style={{ fontSize: '0.6rem' }}>
                                                                <span className="fw-bold text-warning text-uppercase">Top 8 Eligibility</span>
                                                                <span className="text-body-secondary">{playersAtTopCount}/4</span>
                                                            </div>
                                                            <div className="progress bg-dark bg-opacity-10" style={{ height: '4px' }}>
                                                                <div
                                                                    className="progress-bar bg-warning progress-bar-striped progress-bar-animated transition-all"
                                                                    style={{ width: `${(playersAtTopCount / 4) * 100}%` }}
                                                                ></div>
                                                            </div>
                                                        </div>
                                                    )}
                                                    {isDeclined && <div className="small text-danger text-center fw-bold">Player Declined</div>}
                                                    {isCancelled && <div className="small text-muted text-center fw-bold">Request Cancelled</div>}
                                                    {isCompleted && (
                                                        <div className="small text-success text-center fw-bold py-2">
                                                            <i className="bi bi-check-circle-fill me-1"></i> Match Created
                                                        </div>
                                                    )}
                                                </div>

                                                {/* Actions Row */}
                                                <div className="d-flex gap-2">
                                                    {isReady && (
                                                        <button
                                                            className={`btn flex-grow-1 fw-bold rounded-3 py-2 shadow-sm transition-all ${canStart ? 'btn-success' : 'btn-light border text-muted opacity-50'}`}
                                                            onClick={() => canStart && createMatch(req)}
                                                            disabled={!canStart}
                                                            style={{ fontSize: '0.85rem' }}
                                                        >
                                                            {canStart ? 'Start Match' : 'Locked'}
                                                        </button>
                                                    )}

                                                    {!isCancelled && !isCompleted && !isDeclined && (
                                                        <button
                                                            className={`btn rounded-3 fw-bold py-2 ${canStart || !isReady ? 'btn-outline-danger flex-grow-1' : 'btn-outline-secondary px-3'}`}
                                                            onClick={() => cancelRequest(req)}
                                                            style={{ fontSize: '0.85rem' }}
                                                        >
                                                            {isReady && canStart ? "Cancel" : <i className="bi bi-trash"></i>}
                                                            {!isReady && " Cancel Request"}
                                                        </button>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </details>

            <style>{`
                .cursor-pointer { cursor: pointer; }
                .list-unstyled::-webkit-details-marker { display: none; }
                .rotate-180 { transform: rotate(180deg); }
                .transition-all { transition: all 0.3s ease; }
                .fw-black { font-weight: 900; }
                
                .pulse-icon {
                    box-shadow: 0 0 0 0 rgba(var(--bs-primary-rgb), 0.4);
                    animation: pulse-blue 2s infinite;
                }

                @keyframes pulse-blue {
                    0% { box-shadow: 0 0 0 0 rgba(var(--bs-primary-rgb), 0.4); }
                    70% { box-shadow: 0 0 0 10px rgba(var(--bs-primary-rgb), 0); }
                    100% { box-shadow: 0 0 0 0 rgba(var(--bs-primary-rgb), 0); }
                }

                .ready-card-glow { 
                    box-shadow: 0 8px 20px -8px rgba(25, 135, 84, 0.4) !important;
                    border: 1px solid rgba(25, 135, 84, 0.3) !important;
                }
            `}</style>
        </div>
    );
}